/**
 * Generic PDF builder for WhatsApp Send.
 *
 * Renders any document type (sale invoice, quotation, statement, payslip,
 * ledger, etc.) into an A4 PDF as base64 string ready to attach to a
 * WhatsApp message.
 *
 * For transactions, uses a self-contained HTML layout (independent of
 * printTemplates which is print-window based). For statements/ledgers,
 * renders a tabular ledger view.
 */

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { isPrintBrandHidden, drawPdfBrandLine } from './printBranding';

const fmt = (n) => Number(n || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const escapeHtml = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

function companyHeaderHtml(activeCompany) {
  return `
    <div>
      <div style="font-size:22px;font-weight:700;color:#800020;">${escapeHtml(activeCompany?.companyName || 'Company')}</div>
      <div style="font-size:12px;color:#666;margin-top:4px;">${escapeHtml(activeCompany?.address || '')}</div>
      ${activeCompany?.phone ? `<div style="font-size:12px;color:#666;">Phone: ${escapeHtml(activeCompany.phone)}</div>` : ''}
      ${activeCompany?.ntn ? `<div style="font-size:12px;color:#666;">NTN: ${escapeHtml(activeCompany.ntn)}</div>` : ''}
    </div>`;
}

function partyBlockHtml(label, party) {
  if (!party) return '';
  return `
    <div style="flex:1;">
      <div style="font-size:11px;color:#888;text-transform:uppercase;">${escapeHtml(label)}</div>
      <div style="font-size:14px;font-weight:600;margin-top:2px;">${escapeHtml(party.companyName || party.fullName || party.vendorName || party.name || '')}</div>
      ${party.address ? `<div style="font-size:12px;color:#444;">${escapeHtml(party.address)}</div>` : ''}
      ${party.phone ? `<div style="font-size:12px;color:#444;">Phone: ${escapeHtml(party.phone)}</div>` : ''}
      ${party.ntn ? `<div style="font-size:12px;color:#444;">NTN: ${escapeHtml(party.ntn)}</div>` : ''}
    </div>`;
}

function lineItemsTableHtml(lines, currency, opts = {}) {
  const showAmount = opts.showAmount !== false;
  const rows = (lines || []).map((l, i) => `
    <tr>
      <td style="padding:6px 8px;border:1px solid #ddd;text-align:center;">${i + 1}</td>
      <td style="padding:6px 8px;border:1px solid #ddd;">${escapeHtml(l.itemName || l.description || '')}</td>
      <td style="padding:6px 8px;border:1px solid #ddd;text-align:right;">${fmt(l.quantity)}</td>
      ${showAmount ? `<td style="padding:6px 8px;border:1px solid #ddd;text-align:right;">${fmt(l.unitPrice)}</td>` : ''}
      ${showAmount ? `<td style="padding:6px 8px;border:1px solid #ddd;text-align:right;">${fmt((l.quantity || 0) * (l.unitPrice || 0))}</td>` : ''}
    </tr>
  `).join('');
  return `
    <table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:12px;">
      <thead>
        <tr style="background:#f3eef0;color:#800020;">
          <th style="padding:8px;border:1px solid #ddd;width:40px;">#</th>
          <th style="padding:8px;border:1px solid #ddd;text-align:left;">Item</th>
          <th style="padding:8px;border:1px solid #ddd;width:80px;text-align:right;">Qty</th>
          ${showAmount ? `<th style="padding:8px;border:1px solid #ddd;width:110px;text-align:right;">Unit Price</th>` : ''}
          ${showAmount ? `<th style="padding:8px;border:1px solid #ddd;width:130px;text-align:right;">Amount</th>` : ''}
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>`;
}

function totalsHtml(doc, currency, showBalance = false) {
  return `
    <div style="display:flex;justify-content:flex-end;">
      <table style="font-size:13px;border-collapse:collapse;">
        ${doc.subtotal != null ? `<tr><td style="padding:4px 12px;color:#444;">Subtotal</td><td style="padding:4px 12px;text-align:right;">${currency} ${fmt(doc.subtotal)}</td></tr>` : ''}
        ${doc.discountAmount ? `<tr><td style="padding:4px 12px;color:#444;">Discount</td><td style="padding:4px 12px;text-align:right;">- ${currency} ${fmt(doc.discountAmount)}</td></tr>` : ''}
        ${doc.taxAmount ? `<tr><td style="padding:4px 12px;color:#444;">Tax</td><td style="padding:4px 12px;text-align:right;">${currency} ${fmt(doc.taxAmount)}</td></tr>` : ''}
        <tr style="font-weight:700;font-size:15px;border-top:2px solid #800020;">
          <td style="padding:8px 12px;">TOTAL</td>
          <td style="padding:8px 12px;text-align:right;color:#800020;">${currency} ${fmt(doc.totalAmount)}</td>
        </tr>
        ${showBalance && doc.balanceAmount != null ? `<tr><td style="padding:4px 12px;color:#444;">Paid</td><td style="padding:4px 12px;text-align:right;">${currency} ${fmt(doc.paidAmount || 0)}</td></tr>` : ''}
        ${showBalance && doc.balanceAmount != null ? `<tr><td style="padding:4px 12px;color:#444;font-weight:600;">Balance</td><td style="padding:4px 12px;text-align:right;font-weight:600;">${currency} ${fmt(doc.balanceAmount)}</td></tr>` : ''}
      </table>
    </div>`;
}

// ── Document layouts ────────────────────────────────────────────

function buildTransactionHtml({ title, doc, party, partyLabel, activeCompany, hideAmounts = false, showBalance = false }) {
  const currency = activeCompany?.currencySymbol || 'Rs.';
  return `
  <div style="width:794px;padding:32px;font-family:Arial,Helvetica,sans-serif;color:#222;background:#fff;">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #800020;padding-bottom:14px;margin-bottom:18px;">
      ${companyHeaderHtml(activeCompany)}
      <div style="text-align:right;">
        <div style="font-size:24px;font-weight:700;color:#800020;">${escapeHtml(title.toUpperCase())}</div>
        <div style="font-size:13px;margin-top:6px;">No: <b>${escapeHtml(doc.docNumber || '')}</b></div>
        <div style="font-size:13px;">Date: <b>${escapeHtml(doc.docDate || '')}</b></div>
      </div>
    </div>
    <div style="display:flex;gap:24px;margin-bottom:14px;">
      ${partyBlockHtml(partyLabel, party)}
    </div>
    ${lineItemsTableHtml(doc.lines, currency, { showAmount: !hideAmounts })}
    ${!hideAmounts ? totalsHtml(doc, currency, showBalance) : ''}
    ${doc.notes ? `<div style="margin-top:18px;font-size:12px;color:#555;"><b>Notes:</b> ${escapeHtml(doc.notes)}</div>` : ''}
    <div style="margin-top:24px;font-size:11px;color:#999;text-align:center;border-top:1px solid #eee;padding-top:10px;">
      Sent via AMAL ERP &middot; WhatsApp
    </div>
  </div>`;
}

function buildReceiptPaymentHtml({ title, doc, party, partyLabel, activeCompany }) {
  const currency = activeCompany?.currencySymbol || 'Rs.';
  const alloc = (doc.invoiceAllocations || []).map((a, i) => `
    <tr>
      <td style="padding:6px 8px;border:1px solid #ddd;text-align:center;">${i + 1}</td>
      <td style="padding:6px 8px;border:1px solid #ddd;">${escapeHtml(a.invoiceNumber || a.invoiceId || '')}</td>
      <td style="padding:6px 8px;border:1px solid #ddd;text-align:right;">${fmt(a.amount)}</td>
    </tr>`).join('');
  return `
  <div style="width:794px;padding:32px;font-family:Arial,Helvetica,sans-serif;color:#222;background:#fff;">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #800020;padding-bottom:14px;margin-bottom:18px;">
      ${companyHeaderHtml(activeCompany)}
      <div style="text-align:right;">
        <div style="font-size:24px;font-weight:700;color:#800020;">${escapeHtml(title.toUpperCase())}</div>
        <div style="font-size:13px;margin-top:6px;">No: <b>${escapeHtml(doc.docNumber || '')}</b></div>
        <div style="font-size:13px;">Date: <b>${escapeHtml(doc.docDate || '')}</b></div>
      </div>
    </div>
    <div style="display:flex;gap:24px;margin-bottom:14px;">
      ${partyBlockHtml(partyLabel, party)}
    </div>
    <div style="font-size:14px;margin:18px 0;">
      Amount: <b style="color:#800020;">${currency} ${fmt(doc.totalAmount)}</b>
    </div>
    ${alloc ? `
    <div style="margin-top:14px;">
      <div style="font-size:12px;color:#888;margin-bottom:4px;">Applied to invoices:</div>
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead><tr style="background:#f3eef0;color:#800020;">
          <th style="padding:6px;border:1px solid #ddd;width:40px;">#</th>
          <th style="padding:6px;border:1px solid #ddd;text-align:left;">Invoice</th>
          <th style="padding:6px;border:1px solid #ddd;width:130px;text-align:right;">Amount</th>
        </tr></thead>
        <tbody>${alloc}</tbody>
      </table>
    </div>` : ''}
    ${doc.notes ? `<div style="margin-top:18px;font-size:12px;color:#555;"><b>Notes:</b> ${escapeHtml(doc.notes)}</div>` : ''}
    <div style="margin-top:24px;font-size:11px;color:#999;text-align:center;border-top:1px solid #eee;padding-top:10px;">
      Sent via AMAL ERP &middot; WhatsApp
    </div>
  </div>`;
}

function buildStatementHtml({ title, party, partyLabel, rows, openingBalance, closingBalance, periodLabel, activeCompany }) {
  const currency = activeCompany?.currencySymbol || 'Rs.';
  let running = Number(openingBalance || 0);
  const tableRows = (rows || []).map((r, i) => {
    const dr = Number(r.debit || 0);
    const cr = Number(r.credit || 0);
    running += dr - cr;
    return `<tr>
      <td style="padding:5px 8px;border:1px solid #ddd;">${escapeHtml(r.date || '')}</td>
      <td style="padding:5px 8px;border:1px solid #ddd;">${escapeHtml(r.docNumber || '')}</td>
      <td style="padding:5px 8px;border:1px solid #ddd;">${escapeHtml(r.description || r.tranTypeCode || '')}</td>
      <td style="padding:5px 8px;border:1px solid #ddd;text-align:right;">${dr ? fmt(dr) : ''}</td>
      <td style="padding:5px 8px;border:1px solid #ddd;text-align:right;">${cr ? fmt(cr) : ''}</td>
      <td style="padding:5px 8px;border:1px solid #ddd;text-align:right;">${fmt(running)}</td>
    </tr>`;
  }).join('');
  return `
  <div style="width:794px;padding:32px;font-family:Arial,Helvetica,sans-serif;color:#222;background:#fff;">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #800020;padding-bottom:14px;margin-bottom:18px;">
      ${companyHeaderHtml(activeCompany)}
      <div style="text-align:right;">
        <div style="font-size:22px;font-weight:700;color:#800020;">${escapeHtml(title.toUpperCase())}</div>
        ${periodLabel ? `<div style="font-size:13px;margin-top:6px;">Period: <b>${escapeHtml(periodLabel)}</b></div>` : ''}
      </div>
    </div>
    <div style="display:flex;gap:24px;margin-bottom:14px;">
      ${partyBlockHtml(partyLabel, party)}
    </div>
    <table style="width:100%;border-collapse:collapse;font-size:11px;margin-bottom:12px;">
      <thead>
        <tr style="background:#f3eef0;color:#800020;">
          <th style="padding:6px;border:1px solid #ddd;text-align:left;">Date</th>
          <th style="padding:6px;border:1px solid #ddd;text-align:left;">Doc #</th>
          <th style="padding:6px;border:1px solid #ddd;text-align:left;">Description</th>
          <th style="padding:6px;border:1px solid #ddd;text-align:right;">Debit</th>
          <th style="padding:6px;border:1px solid #ddd;text-align:right;">Credit</th>
          <th style="padding:6px;border:1px solid #ddd;text-align:right;">Balance</th>
        </tr>
      </thead>
      <tbody>
        <tr style="background:#fafafa;">
          <td colspan="5" style="padding:5px 8px;border:1px solid #ddd;"><b>Opening Balance</b></td>
          <td style="padding:5px 8px;border:1px solid #ddd;text-align:right;"><b>${fmt(openingBalance || 0)}</b></td>
        </tr>
        ${tableRows}
        <tr style="background:#f3eef0;">
          <td colspan="5" style="padding:6px 8px;border:1px solid #ddd;"><b>Closing Balance</b></td>
          <td style="padding:6px 8px;border:1px solid #ddd;text-align:right;font-weight:700;color:#800020;">${fmt(closingBalance != null ? closingBalance : running)}</td>
        </tr>
      </tbody>
    </table>
    <div style="margin-top:24px;font-size:11px;color:#999;text-align:center;border-top:1px solid #eee;padding-top:10px;">
      Sent via AMAL ERP &middot; WhatsApp
    </div>
  </div>`;
}

function buildPayslipHtml({ doc, staff, activeCompany, monthLabel }) {
  const currency = activeCompany?.currencySymbol || 'Rs.';
  const earnings = doc.earnings || doc.fnfEarnings || [];
  const deductions = doc.deductions || doc.fnfDeductions || [];
  const earningsRows = earnings.map(e => `
    <tr>
      <td style="padding:5px 8px;border:1px solid #ddd;">${escapeHtml(e.name)}</td>
      <td style="padding:5px 8px;border:1px solid #ddd;text-align:right;">${fmt(e.amount)}</td>
    </tr>`).join('');
  const deductionRows = deductions.map(d => `
    <tr>
      <td style="padding:5px 8px;border:1px solid #ddd;">${escapeHtml(d.name)}</td>
      <td style="padding:5px 8px;border:1px solid #ddd;text-align:right;">${fmt(d.amount)}</td>
    </tr>`).join('');
  return `
  <div style="width:794px;padding:32px;font-family:Arial,Helvetica,sans-serif;color:#222;background:#fff;">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #800020;padding-bottom:14px;margin-bottom:18px;">
      ${companyHeaderHtml(activeCompany)}
      <div style="text-align:right;">
        <div style="font-size:22px;font-weight:700;color:#800020;">PAYSLIP</div>
        ${monthLabel ? `<div style="font-size:13px;margin-top:6px;">For: <b>${escapeHtml(monthLabel)}</b></div>` : ''}
        ${doc.docNumber ? `<div style="font-size:13px;">No: <b>${escapeHtml(doc.docNumber)}</b></div>` : ''}
      </div>
    </div>
    <div style="margin-bottom:14px;">
      <div style="font-size:14px;font-weight:600;">${escapeHtml(staff?.staffName || staff?.fullName || '')}</div>
      ${staff?.designation ? `<div style="font-size:12px;color:#666;">${escapeHtml(staff.designation)}</div>` : ''}
      ${staff?.cnic ? `<div style="font-size:12px;color:#666;">CNIC: ${escapeHtml(staff.cnic)}</div>` : ''}
    </div>
    <div style="display:flex;gap:18px;margin-bottom:18px;">
      <div style="flex:1;">
        <div style="font-size:12px;color:#888;margin-bottom:4px;">EARNINGS</div>
        <table style="width:100%;border-collapse:collapse;font-size:12px;">
          ${earningsRows}
          <tr style="background:#f3eef0;font-weight:600;">
            <td style="padding:6px 8px;border:1px solid #ddd;">Gross</td>
            <td style="padding:6px 8px;border:1px solid #ddd;text-align:right;">${fmt(doc.grossAmount || doc.totalEarnings)}</td>
          </tr>
        </table>
      </div>
      <div style="flex:1;">
        <div style="font-size:12px;color:#888;margin-bottom:4px;">DEDUCTIONS</div>
        <table style="width:100%;border-collapse:collapse;font-size:12px;">
          ${deductionRows}
          <tr style="background:#f3eef0;font-weight:600;">
            <td style="padding:6px 8px;border:1px solid #ddd;">Total Deductions</td>
            <td style="padding:6px 8px;border:1px solid #ddd;text-align:right;">${fmt(doc.totalDeductions)}</td>
          </tr>
        </table>
      </div>
    </div>
    <div style="display:flex;justify-content:flex-end;">
      <table style="font-size:14px;font-weight:700;border-collapse:collapse;">
        <tr style="background:#800020;color:#fff;">
          <td style="padding:10px 14px;">NET PAYABLE</td>
          <td style="padding:10px 14px;text-align:right;">${currency} ${fmt(doc.netPayable || doc.totalAmount)}</td>
        </tr>
      </table>
    </div>
    <div style="margin-top:24px;font-size:11px;color:#999;text-align:center;border-top:1px solid #eee;padding-top:10px;">
      Sent via AMAL ERP &middot; WhatsApp
    </div>
  </div>`;
}

// ── HTML → PDF base64 ───────────────────────────────────────────

export async function htmlToPdfBase64(html, { brand = false } = {}) {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.style.top = '0';
  container.style.background = '#fff';
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    const target = container.firstElementChild;
    const canvas = await html2canvas(target, { scale: 2, useCORS: true, allowTaint: true, backgroundColor: '#ffffff', logging: false });
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const imgWidth = pageWidth - margin * 2;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const imgData = canvas.toDataURL('image/png');

    if (imgHeight <= pageHeight - margin * 2) {
      pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight);
    } else {
      let remaining = imgHeight;
      let position = margin;
      pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
      remaining -= (pageHeight - margin * 2);
      while (remaining > 0) {
        pdf.addPage();
        position = -(imgHeight - remaining) + margin;
        pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
        remaining -= (pageHeight - margin * 2);
      }
    }
    // "Powered by AmalERP" at the bottom of every page (see printBranding.js),
    // on a white strip in the bottom margin — the rows a long PDF shows there
    // are repeated at the top of the next page, so nothing is lost.
    if (brand) {
      const pages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pages; i += 1) {
        pdf.setPage(i);
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, pageHeight - 7, pageWidth, 7, 'F');
        drawPdfBrandLine(pdf, pageWidth, pageHeight - 3.5);
      }
    }
    const dataUri = pdf.output('datauristring');
    return dataUri.split(',').pop();
  } finally {
    document.body.removeChild(container);
  }
}

// ── Public entry: build PDF for a document ──────────────────────

const DOC_TITLES = {
  saleInvoice: 'Sale Invoice', nonTaxSaleInvoice: 'Sale Invoice', saleReturn: 'Sale Return', nonTaxSaleReturn: 'Sale Return',
  quotation: 'Quotation', saleOrder: 'Sale Order', deliveryChallan: 'Delivery Challan',
  purchaseInvoice: 'Purchase Invoice', nonTaxPurchaseInvoice: 'Purchase Invoice', purchaseReturn: 'Purchase Return', nonTaxPurchaseReturn: 'Purchase Return',
  purchaseOrder: 'Purchase Order', grn: 'Goods Receipt',
  cashReceipt: 'Cash Receipt', bankReceipt: 'Bank Receipt', cashPayment: 'Cash Payment Voucher', bankPayment: 'Bank Payment Voucher',
};

const PARTY_LABELS = {
  saleInvoice: 'Bill To', nonTaxSaleInvoice: 'Bill To', saleReturn: 'From', nonTaxSaleReturn: 'From',
  quotation: 'Quoted To', saleOrder: 'Customer', deliveryChallan: 'Deliver To',
  purchaseInvoice: 'Supplier', nonTaxPurchaseInvoice: 'Supplier', purchaseReturn: 'Return To', nonTaxPurchaseReturn: 'Return To',
  purchaseOrder: 'Supplier', grn: 'Supplier',
  cashReceipt: 'Received From', bankReceipt: 'Received From', cashPayment: 'Paid To', bankPayment: 'Paid To',
};

const RECEIPT_PAYMENT_DOC_TYPES = new Set(['cashReceipt', 'bankReceipt', 'cashPayment', 'bankPayment']);
const SHOW_BALANCE_DOC_TYPES = new Set(['saleInvoice', 'nonTaxSaleInvoice', 'purchaseInvoice', 'nonTaxPurchaseInvoice']);
const HIDE_AMOUNTS_DOC_TYPES = new Set(['deliveryChallan', 'grn']);

/**
 * Build a PDF for a document.
 * @param {string} docType - one of the keys in DOC_TITLES (or 'statement' / 'payslip')
 * @param {object} doc - the transaction/document object
 * @param {object} party - customer/vendor/staff master record
 * @param {object} activeCompany
 * @param {object} extras - { rows, openingBalance, closingBalance, periodLabel, monthLabel }
 * @returns {Promise<string>} base64-encoded PDF
 */
export async function buildDocumentPdfBase64(docType, doc, party, activeCompany, extras = {}) {
  let html;
  if (docType === 'statement' || docType === 'ledger') {
    html = buildStatementHtml({
      title: extras.title || 'Account Statement',
      party,
      partyLabel: extras.partyLabel || 'Account',
      rows: extras.rows || [],
      openingBalance: extras.openingBalance || 0,
      closingBalance: extras.closingBalance,
      periodLabel: extras.periodLabel,
      activeCompany
    });
  } else if (docType === 'payslip') {
    html = buildPayslipHtml({ doc, staff: party, activeCompany, monthLabel: extras.monthLabel });
  } else if (RECEIPT_PAYMENT_DOC_TYPES.has(docType)) {
    html = buildReceiptPaymentHtml({
      title: DOC_TITLES[docType] || docType,
      doc, party, partyLabel: PARTY_LABELS[docType] || 'Party', activeCompany
    });
  } else {
    html = buildTransactionHtml({
      title: DOC_TITLES[docType] || docType,
      doc, party, partyLabel: PARTY_LABELS[docType] || 'Party',
      activeCompany,
      hideAmounts: HIDE_AMOUNTS_DOC_TYPES.has(docType),
      showBalance: SHOW_BALANCE_DOC_TYPES.has(docType)
    });
  }
  return await htmlToPdfBase64(html, { brand: !isPrintBrandHidden(activeCompany) });
}
